<template>
  <mf-loading-dialog :loading="loadingDialog">
    <base-page-layout
      v-if="campaign._id"
      ref="base-page"
      :title="$t('campanhas.resultados_campanha')"
      :hide-breadcrumbs="$getFirstValue()"
      hide-ecommerce-configuration
      use-new-filters
      :filters="['lojas']"
      :filter-to-units="campaignUnitIds"
    >
      <div class="info-message">
        <mf-icon fill>notification_important</mf-icon>
        <span>Mensagens entregues e resultados das campanhas se dão em até 72h, após o término da ação.</span>
      </div>
      <v-row>
        <v-col>
          <info-card
            :title="campaign.title"
            :description="campaign.description"
            :date="campaign.message.send_date"
            :time="campaign.message.send_time"
            :segments="campaign.segments"
            :products="campaign.products"
            :initial-date="campaign.initial_date"
            :final-date="campaign.final_date"
            :status="campaign.status"
            :frequency-type="campaign.frequency.type"
            :frequency-interval="campaign.frequency.interval"
            :message-infos="messageInfos"
            :with-margins="false"
            :industry="campaign.industry"
            :frequency="campaign.frequency"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="pl-4">
          <span class="section-title">Resultados gerais</span>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-for="(card, index) in summaryCards" :key="`summary-card-${index}`">
          <cbm-card
            :number-text="card.value"
            :title="card.text"
            :tooltip="card.helpText"
            :boolean-loader="loadingMetrics"
            :quick-action="card.quickAction"
            :is-currency="card.isCurrency"
            :percent-text="card.underText"
            :percent-description="card.description"
            unset-percentage-colors
            simple-card
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <funnel :loading="loadingMetrics" :metrics="metrics" :campaign="campaign" :units-configuration="unitsConfiguration" />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <products-table :loading="loadingMetrics" :metrics="metrics" :campaign="campaign" :units-configuration="unitsConfiguration" />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <sendings
            :loading="loadingMetrics"
            :sendings="sendings"
            :campaign-id="getSafe(() => campaign._id)"
            :industry="campaign.industry"
            campaign-type="sms"
          />
        </v-col>
      </v-row>

      <mf-action-buttons :primary-button="primaryButton" :cancel-button="cancelButton" />
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import { QUERY_GET_CAMPAIGN_METRICS } from '@/modules/campanhas/views/components/graphql'
import { localeString } from '@/config/helpers.js'
import { criteriaGroup, campaignReceivedAndPurchasedCriteria, isInUnitsCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { actionButtons } from '../../../views/mixins/action-buttons.js'
import { DateTime } from 'luxon'
import { mapState } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import { QUERY_GET_CAMPAIGN_BY_ID } from '@/modules/campanhas/campanhas-sms/graphql'
import offerScheduleCampaign from '@/modules/campanhas/views/mixins/offer-schedule-campaign.js'

export default {
  components: {
    BasePageLayout,
    InfoCard: () => import('@/modules/campanhas/views/components/results/InfoCard.vue'),
    Funnel: () => import('@/modules/campanhas/views/components/results/Funnel.vue'),
    Sendings: () => import('@/modules/campanhas/views/components/results/Sendings.vue'),
    ProductsTable: () => import('@/modules/campanhas/views/components/results/ProductsTable.vue'),
    CbmCard: () => import('@/components/CbmCard.vue'),
  },

  mixins: [actionButtons, offerScheduleCampaign],

  data: () => ({
    sendings: [],
    campaignUnitIds: [],
    locale: localeString,
    redirectPage: 'sms',

    loadingMetrics: false,
    loadingDialog: false,
    metrics: {},
    campaignId: '',
    campaign: {
      title: '',
      description: '',
      initial_date: '',
      final_date: '',
      products: [],
      segments: [],
      template_id: '',
      status: 'DRAFT',
      frequency: {
        type: 'ONETIME',
        interval: 1,
      },
      message: {
        text: '',
        send_date: '',
        send_time: '',
      },
      offers_schedule_id: '',
      industry: null,
    },
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    unitsConfiguration() {
      return this.stateFilters.unitsConfiguration
    },
    messageInfos() {
      return [
        {
          cols: '12',
          header: 'Mensagem',
          description: this.campaign.message.text,
        },
      ]
    },
    summaryCards() {
      const initialDate = DateTime.fromFormat(this.campaign.initial_date, 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const finalDate = DateTime.fromFormat(this.campaign.final_date, 'yyyy-MM-dd').toFormat('yyyyMMdd')

      let alavancagem
      if (this.campaign.industry || this.metrics?.analytic_metrics?.price === undefined) {
        alavancagem = 0
      } else {
        alavancagem = this.metrics?.analytic_metrics?.price
      }

      return [
        {
          value: localeString(this.metrics?.clients_metrics?.received_and_bought?.count || 0),
          text: 'Clientes que compraram',
          underText: `${this.locale(
            this.getSafe(() => this.metrics?.clients_metrics?.received_and_bought?.count / this.metrics?.clients_metrics?.received?.count) * 100 || 0,
            1
          )}%`,
          description: `dos ${this.locale(this.metrics?.clients_metrics?.received?.count || 0)} clientes que receberam`,
          helpText: 'Quantidade de clientes que receberam SMS e realizaram pelo menos uma compra no período de mensuração',
          isInteger: true,
          quickAction: {
            id: 'received_and_bought',
            shortDescription: `Clientes que haviam recebido a campanha ${this.campaign.title} e compraram no período de mensuração`,
            longDescription: `Clientes que haviam recebido a campanha ${this.campaign.title} e compraram no período de mensuração entre ${DateTime.fromFormat(
              this.campaign.initial_date,
              'yyyy-MM-dd'
            ).toFormat('dd/MM/yyyy')} e ${DateTime.fromFormat(this.campaign.final_date, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')}`,
            segmentationGroup: criteriaGroup([
              campaignReceivedAndPurchasedCriteria([this.campaign._id]),
              ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, initialDate, finalDate) : []),
            ]),
          },
        },
        {
          value: this.locale(this.metrics?.clients_metrics?.received_and_bought?.average_ticket || 0, 2),
          text: 'Ticket médio',
          underText: `${this.locale(this.metrics?.analytic_metrics?.average_ticket_received_variation, 1)}%`,
          description: `a ${this.metrics?.analytic_metrics?.average_ticket_received_variation > 0 ? 'mais' : 'menos'} do que quem não recebeu`,
          helpText:
            'Ticket médio dos clientes que receberam a campanha e realizaram pelo menos uma compra no período de mensuração em comparação com o ticket médio dos consumidores do clube no mesmo período dessa campanha',
          isCurrency: true,
        },
        {
          value: localeString(this.metrics?.clients_metrics?.received_and_bought?.revenue || 0, 0),
          text: 'Faturamento',
          underText: `${this.locale(
            this.getSafe(() => this.metrics?.clients_metrics?.received_and_bought?.revenue / this.metrics?.clients_metrics?.total?.revenue) * 100 || 0,
            1
          )}%`,
          description: `do faturamento total`,
          helpText:
            'Faturamento dos clientes que receberam e compraram em comparação com o faturamento dos consumidores do clube no mesmo período dessa campanha',
          isCurrency: true,
        },
        {
          value: this.campaign.industry ? '-' : localeString(this.metrics?.analytic_metrics?.received_roi || 0),
          text: 'Alavancagem',
          helpText: 'Faturamento gerado para cada real investido na campanha',
          description: `investimento da campanha`,
          underText: `R$ ${this.localeString(alavancagem, 0)}`,
          isInteger: true,
        },
      ]
    },
    units() {
      return this.stateFilters.units || []
    },
  },
  watch: {
    $route(route) {
      if (route.params?.id?.length !== 24 && route.query?.quick_action) {
        // Reset the data
        Object.assign(this.$data, this.$options.data.apply(this))
      }
    },
  },
  async mounted() {
    this.campaignId = this.$route.params.id
    if (this.campaignId?.length !== 24) this.campaignId = ''
    try {
      this.loadingDialog = true
      await this.getCampaign()
      await this.getCampaignMetrics()
    } catch (error) {
      console.log(error)
    } finally {
      this.loadingDialog = false
    }
    if (this.campaign.messages_queues) this.formatData()

    this.$bus.$on('filter', this.handleFilterEvent)
  },
  destroyed() {
    this.$bus.$off('filter')
  },
  methods: {
    handleFilterEvent(payload = {}) {
      if (payload.isInitialLoad) return
      this.getCampaignMetrics()
    },
    async getCampaign() {
      const {
        data: { campaign },
      } = await this.$apollo.query({
        query: QUERY_GET_CAMPAIGN_BY_ID,
        notifyOnNetworkStatusChange: true,
        context: {
          uri: this.$microservicesUrls['campaigns'],
        },
        variables: {
          id: this.campaignId,
        },
        fetchPolicy: 'no-cache',
      })
      let newCampaign = cloneDeep(campaign)

      if (newCampaign && newCampaign.segments) {
        newCampaign.segments = newCampaign.segments.map((segment) => {
          segment.updating = false
          return segment
        })
      }

      if (newCampaign?.offers_schedule && Object.entries(newCampaign.offers_schedule).length) {
        newCampaign = { ...newCampaign, offers_schedule_id: newCampaign.offers_schedule._id }
        this.offerSchedule = newCampaign.offers_schedule
      } else {
        newCampaign = { ...newCampaign, offers_schedule_id: '' }
        this.offerSchedule = {}
      }

      delete newCampaign.offers_schedule
      this.campaign = cloneDeep(newCampaign)
      if (newCampaign.units?.length) this.campaignUnitIds = newCampaign.units.map((unit) => unit.id_erp)
    },
    async getCampaignMetrics() {
      try {
        this.loadingMetrics = true
        const {
          data: { campaign },
        } = await this.$apollo.query({
          query: QUERY_GET_CAMPAIGN_METRICS,
          notifyOnNetworkStatusChange: true,
          variables: {
            id: this.campaign._id,
            units: this.units,
          },
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
          fetchPolicy: 'no-cache',
        })
        if (!campaign) return
        this.sendings = campaign?.message?.sendings || []
        this.metrics = campaign.metrics
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingMetrics = false
      }
    },
  },
}
</script>

<style lang="scss">
.info-message {
  i {
    opacity: 0.5;
    margin-bottom: 4px;
    margin-right: 4px;
  }
  span {
    opacity: 0.8;
  }
}
</style>
